/**
 * GUFUMART Product Gallery — Milestone 1
 * Additive layer on Dawn MediaGallery + product-modal.
 * Single init system; no duplicate listeners.
 */
(function () {
  'use strict';

  var ROOT_INIT = 'data-gf-gallery-root-init';
  var GALLERY_INIT = 'data-gf-gallery-init';
  var scrollLockCount = 0;
  var savedScrollY = 0;

  /* ── Utilities ───────────────────────────────────────────── */

  function gfDebounce(fn, wait) {
    var timer;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function shopifyPriceToCents(value) {
    if (value == null || value === '') return null;
    var str = String(value).trim();
    var amount = parseFloat(str.replace(/,/g, ''));
    if (!isFinite(amount)) return null;

    /* /collections/.../products.json → decimal strings in major units ("299.00") */
    if (str.indexOf('.') !== -1) {
      return Math.round(amount * 100);
    }

    /*
     * /recommendations/products.json and /products/{handle}.js → integer cents (29900).
     * Whole numbers under 1000 are treated as major units ("299" → 299.00).
     */
    if (amount >= 1000) {
      return Math.round(amount);
    }

    return Math.round(amount * 100);
  }

  function formatShopMoney(cents) {
    var value = Number(cents);
    if (!isFinite(value)) return '';
    var roundedCents = Math.round(value);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(roundedCents);
    }
    var major = roundedCents / 100;
    var isWhole = Math.abs(major - Math.round(major)) < 0.001;
    var currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: isWhole ? 0 : 2,
        maximumFractionDigits: isWhole ? 0 : 2,
      }).format(major);
    } catch (err) {
      return isWhole ? String(Math.round(major)) : major.toFixed(2);
    }
  }

  function formatSimilarMoney(cents) {
    var value = Number(cents);
    if (!isFinite(value)) return '';
    var major = Math.round(value) / 100;
    var isWhole = Math.abs(major - Math.round(major)) < 0.001;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: isWhole ? 0 : 2,
        maximumFractionDigits: isWhole ? 0 : 2,
      }).format(major);
    } catch (err) {
      var abs = isWhole ? String(Math.round(Math.abs(major))) : Math.abs(major).toFixed(2);
      return (major < 0 ? '-$' : '$') + abs;
    }
  }

  function getGalleryId(gallery) {
    return (gallery.id || '').replace('MediaGallery-', '');
  }

  function toGalleryMediaId(gallery, rawId) {
    var sectionId = getGalleryId(gallery);
    var id = String(rawId);
    if (id.indexOf(sectionId + '-') === 0) return id;
    return sectionId + '-' + id;
  }

  function lockBodyScroll() {
    scrollLockCount += 1;
    if (scrollLockCount === 1) {
      savedScrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + savedScrollY + 'px';
      document.body.style.width = '100%';
    }
  }

  function unlockBodyScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, savedScrollY);
    }
  }

  /* ── Modal media helpers ─────────────────────────────────── */

  function isGalleryHidden(el) {
    return !el || el.classList.contains('gf-color-gallery-hidden') || el.hasAttribute('hidden');
  }

  function getVisibleSlides(viewer) {
    return Array.from(viewer.querySelectorAll('.product__media-list .slider__slide')).filter(function (slide) {
      return !isGalleryHidden(slide);
    });
  }

  function getModalMediaItems(modal) {
    return Array.from(modal.querySelectorAll('.product-media-modal__content [data-media-id]')).filter(function (el) {
      return !isGalleryHidden(el);
    });
  }

  function updateGalleryCounters(gallery) {
    var viewer = gallery.querySelector('[id^="GalleryViewer-"]');
    if (!viewer) return;
    var slides = getVisibleSlides(viewer);
    var active = viewer.querySelector('.slider__slide.is-active:not(.gf-color-gallery-hidden)');
    var idx = active ? slides.indexOf(active) : 0;
    var current = String(Math.max(1, idx + 1));
    var total = String(Math.max(1, slides.length));
    gallery.querySelectorAll('.slider-counter--current').forEach(function (el) {
      el.textContent = current;
    });
    gallery.querySelectorAll('.slider-counter--total').forEach(function (el) {
      el.textContent = total;
    });
  }

  function refreshProductGallery(gallery) {
    if (!gallery) return;
    var viewer = gallery.querySelector('[id^="GalleryViewer-"]');
    var thumbs = gallery.querySelector('[id^="GalleryThumbnails-"]');
    if (viewer && typeof viewer.resetPages === 'function') viewer.resetPages();
    if (thumbs && typeof thumbs.resetPages === 'function') thumbs.resetPages();
    updateGalleryCounters(gallery);
    initDots(gallery);
    initViewSimilarCollapse(gallery);
  }

  window.gfRefreshProductGallery = refreshProductGallery;

  function updateModalChrome(modal) {
    var items = getModalMediaItems(modal);
    var active = modal.querySelector('.product-media-modal__content [data-media-id].active');
    var idx = active ? items.indexOf(active) : 0;
    var currentEl = modal.querySelector('[data-gf-modal-current]');
    var totalEl = modal.querySelector('[data-gf-modal-total]');
    if (currentEl) currentEl.textContent = String(idx + 1);
    if (totalEl) totalEl.textContent = String(items.length || 1);

    var dots = modal.querySelector('.gf-modal-dots');
    if (!dots) return;
    if (items.length < 2) {
      dots.innerHTML = '';
      return;
    }
    if (dots.children.length !== items.length) {
      dots.innerHTML = items
        .map(function (_, i) {
          return '<span class="gf-dot' + (i === idx ? ' is-active' : '') + '"></span>';
        })
        .join('');
    } else {
      Array.from(dots.children).forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === idx);
      });
    }
  }

  function setModalActiveMedia(modal, rawMediaId) {
    var items = getModalMediaItems(modal);
    items.forEach(function (el) {
      el.classList.toggle('active', el.dataset.mediaId === String(rawMediaId));
    });
    var active = modal.querySelector('[data-media-id="' + rawMediaId + '"].active');
    if (active) active.scrollIntoView({ block: 'nearest', inline: 'center' });
    updateModalChrome(modal);
  }

  function navigateModalMedia(modal, gallery, direction) {
    var items = getModalMediaItems(modal);
    if (items.length < 2) return;

    var active = modal.querySelector('.product-media-modal__content [data-media-id].active');
    var currentIdx = active ? items.indexOf(active) : 0;
    var nextIdx;

    if (direction === 'next') {
      nextIdx = (currentIdx + 1) % items.length;
    } else {
      nextIdx = (currentIdx - 1 + items.length) % items.length;
    }

    var rawId = items[nextIdx].dataset.mediaId;
    var galleryId = toGalleryMediaId(gallery, rawId);

    if (gallery && typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(galleryId, false);
    }

    setModalActiveMedia(modal, rawId);
  }

  /* ── A. Mobile dot indicators ────────────────────────────── */

  function initDots(gallery) {
    var dotsContainer = gallery.querySelector('.gf-slide-dots');
    if (!dotsContainer) return;

    var viewer = gallery.querySelector('[id^="GalleryViewer-"]');
    if (!viewer) return;

    var slides = getVisibleSlides(viewer);
    if (slides.length < 2) {
      dotsContainer.style.display = 'none';
      dotsContainer.innerHTML = '';
      return;
    }

    dotsContainer.style.display = '';

    dotsContainer.innerHTML = '';
    var dots = slides.map(function (slide, i) {
      var dot = document.createElement('span');
      var mediaType = slide.getAttribute('data-gf-media-type') || '';
      var isVideo = mediaType === 'video' || mediaType === 'external_video';
      dot.className = 'gf-dot' + (isVideo ? ' gf-dot--video' : '') + (i === 0 ? ' is-active' : '');
      dot.setAttribute('aria-hidden', 'true');
      dotsContainer.appendChild(dot);
      return dot;
    });

    if (!('IntersectionObserver' in window)) return;

    if (gallery._gfDotsObserver) gallery._gfDotsObserver.disconnect();

    var list = viewer.querySelector('.product__media-list');
    var io = new IntersectionObserver(
      gfDebounce(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var idx = slides.indexOf(entry.target);
          if (idx === -1) return;
          dots.forEach(function (d, i) {
            d.classList.toggle('is-active', i === idx);
          });
          updateViewSimilarButton(gallery, idx);
          updateMediaOverlays(gallery, idx);
        }, 80);
      }),
      { root: list, threshold: 0.55 }
    );

    slides.forEach(function (slide) {
      io.observe(slide);
    });
    gallery._gfDotsObserver = io;
  }

  function updateViewSimilarButton(gallery, slideIndex) {
    var btn = gallery.querySelector('.gf-view-similar-btn');
    if (!btn) return;
    scheduleViewSimilarCollapse(btn);
  }

  function scheduleViewSimilarCollapse(btn) {
    if (!btn || btn.hasAttribute('data-gf-collapse-scheduled')) return;
    btn.setAttribute('data-gf-collapse-scheduled', 'true');
    btn.classList.remove('is-icon-only');
    window.setTimeout(function () {
      if (!btn.isConnected) return;
      btn.classList.add('is-icon-only');
    }, 2500);
  }

  function updateMediaOverlays(gallery, slideIndex) {
    var viewer = gallery.querySelector('[id^="GalleryViewer-"]');
    if (!viewer) return;
    var slides = getVisibleSlides(viewer);
    var slide = slides[slideIndex] || slides[0];
    var overlay = gallery.querySelector('.gf-gallery-overlay');
    if (!slide) return;

    var mediaType = slide.getAttribute('data-gf-media-type') || '';
    var isVideo = mediaType === 'video' || mediaType === 'external_video';
    var isHighlight = slide.getAttribute('data-gf-highlight') === 'true';

    if (overlay) {
      overlay.classList.toggle('is-media-hidden', isVideo || isHighlight);
    }

    pauseInactiveVideos(slides, slide);
    if (isVideo) playSlideVideoMuted(slide);
  }

  function pauseInactiveVideos(slides, activeSlide) {
    slides.forEach(function (slide) {
      if (slide === activeSlide) return;
      var video = slide.querySelector('video');
      if (video && !video.paused) {
        try {
          video.pause();
        } catch (err) {}
      }
    });
  }

  function playSlideVideoMuted(slide) {
    var deferred = slide.querySelector('deferred-media');
    if (deferred && typeof deferred.loadContent === 'function' && !deferred.hasAttribute('loaded')) {
      try {
        deferred.loadContent(false);
      } catch (err) {}
    }

    window.setTimeout(function () {
      var video = slide.querySelector('video');
      if (!video) return;
      video.muted = true;
      video.setAttribute('muted', '');
      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {});
      }
      syncVideoSoundButton(slide, true);
    }, 60);
  }

  function syncVideoSoundButton(slide, muted) {
    var btn = slide.querySelector('[data-gf-video-sound]');
    if (!btn) return;
    var mutedIcon = btn.querySelector('.gf-video-sound__muted');
    var soundIcon = btn.querySelector('.gf-video-sound__on');
    if (mutedIcon) mutedIcon.hidden = !muted;
    if (soundIcon) soundIcon.hidden = !!muted;
    btn.setAttribute('aria-pressed', muted ? 'false' : 'true');
    btn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
  }

  function toggleVideoSound(btn) {
    var slide = btn.closest('.product__media-item');
    if (!slide) return;
    var deferred = slide.querySelector('deferred-media');
    if (deferred && typeof deferred.loadContent === 'function' && !deferred.hasAttribute('loaded')) {
      try {
        deferred.loadContent(false);
      } catch (err) {}
    }

    window.setTimeout(function () {
      var video = slide.querySelector('video');
      if (!video) return;
      video.muted = !video.muted;
      if (!video.muted) {
        var playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {});
        }
      }
      syncVideoSoundButton(slide, video.muted);
    }, 40);
  }

  function initViewSimilarCollapse(gallery) {
    var viewer = gallery.querySelector('[id^="GalleryViewer-"]');
    if (!viewer || !gallery.querySelector('.gf-view-similar-btn')) return;

    if (gallery._gfSlideHandler) {
      viewer.removeEventListener('slideChanged', gallery._gfSlideHandler);
    }

    gallery._gfSlideHandler = function (event) {
      var slides = getVisibleSlides(viewer);
      var idx = slides.indexOf(event.detail.currentElement);
      if (idx !== -1) {
        updateViewSimilarButton(gallery, idx);
        updateMediaOverlays(gallery, idx);
        updateGalleryCounters(gallery);
      }
    };

    viewer.addEventListener('slideChanged', gallery._gfSlideHandler);

    var slides = getVisibleSlides(viewer);
    var active = viewer.querySelector('.slider__slide.is-active:not(.gf-color-gallery-hidden)') || slides[0];
    var initialIdx = active ? slides.indexOf(active) : 0;
    updateViewSimilarButton(gallery, initialIdx >= 0 ? initialIdx : 0);
    updateMediaOverlays(gallery, initialIdx >= 0 ? initialIdx : 0);
  }

  /* ── B. Thumbnail overflow fades ─────────────────────────── */

  function initThumbnailOverflow(gallery) {
    var thumbnailSlider = gallery.querySelector('[id^="GalleryThumbnails-"]');
    if (!thumbnailSlider) return;

    var list = thumbnailSlider.querySelector('.thumbnail-list');
    if (!list) return;

    function updateFades() {
      var scrollLeft = list.scrollLeft;
      var maxScroll = list.scrollWidth - list.clientWidth;
      thumbnailSlider.classList.toggle('gf-thumbnails-overflow-left', scrollLeft > 4);
      thumbnailSlider.classList.toggle('gf-thumbnails-overflow-right', maxScroll - scrollLeft > 4);
    }

    updateFades();
    list.addEventListener('scroll', gfDebounce(updateFades, 60), { passive: true });

    if ('ResizeObserver' in window) {
      if (gallery._gfThumbRO) gallery._gfThumbRO.disconnect();
      var ro = new ResizeObserver(gfDebounce(updateFades, 120));
      ro.observe(list);
      gallery._gfThumbRO = ro;
    }
  }

  /* ── C. Fullscreen / lightbox enhancements ───────────────── */

  var modalState = {
    modal: null,
    gallery: null,
    keyHandler: null,
    swipeStartX: 0,
    swipeStartY: 0,
    swipeTracking: false,
  };

  function initLightboxEnhancements(gallery) {
    var modal = document.querySelector('#ProductModal-' + getGalleryId(gallery));
    if (!modal || modal.hasAttribute('data-gf-modal-init')) return;
    modal.setAttribute('data-gf-modal-init', 'true');

    var prevBtn = modal.querySelector('.gf-modal-nav--prev');
    var nextBtn = modal.querySelector('.gf-modal-nav--next');

    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        navigateModalMedia(modal, gallery, 'prev');
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        navigateModalMedia(modal, gallery, 'next');
      });
    }

    var content = modal.querySelector('.product-media-modal__content');
    if (content && !content.hasAttribute('data-gf-swipe-init')) {
      content.setAttribute('data-gf-swipe-init', 'true');
      content.addEventListener(
        'touchstart',
        function (e) {
          if (!modal.hasAttribute('open')) return;
          modalState.swipeTracking = true;
          modalState.swipeStartX = e.changedTouches[0].screenX;
          modalState.swipeStartY = e.changedTouches[0].screenY;
        },
        { passive: true }
      );
      content.addEventListener(
        'touchend',
        function (e) {
          if (!modalState.swipeTracking || !modal.hasAttribute('open')) return;
          modalState.swipeTracking = false;
          var dx = e.changedTouches[0].screenX - modalState.swipeStartX;
          var dy = e.changedTouches[0].screenY - modalState.swipeStartY;
          if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
          navigateModalMedia(modal, gallery, dx < 0 ? 'next' : 'prev');
        },
        { passive: true }
      );
    }

    if (gallery._gfLightboxMO) gallery._gfLightboxMO.disconnect();

    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName !== 'open') return;
        if (modal.hasAttribute('open')) {
          modalState.modal = modal;
          modalState.gallery = gallery;
          attachLightboxKeys(modal, gallery);
          modal.classList.add('gf-modal-open');
          lockBodyScroll();
          initModalZoom(modal);
          updateModalChrome(modal);
        } else {
          detachLightboxKeys();
          modal.classList.remove('gf-modal-open');
          resetModalZoom(modal);
          modalState.modal = null;
          modalState.gallery = null;
          unlockBodyScroll();
        }
      });
    });
    mo.observe(modal, { attributes: true });
    gallery._gfLightboxMO = mo;
  }

  function attachLightboxKeys(modal, gallery) {
    detachLightboxKeys();
    modalState.keyHandler = function (e) {
      if (!modal.hasAttribute('open')) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigateModalMedia(modal, gallery, 'prev');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateModalMedia(modal, gallery, 'next');
      }
    };
    document.addEventListener('keydown', modalState.keyHandler);
  }

  function detachLightboxKeys() {
    if (modalState.keyHandler) {
      document.removeEventListener('keydown', modalState.keyHandler);
      modalState.keyHandler = null;
    }
  }

  function getModalZoomImage(modal) {
    return modal.querySelector('.product-media-modal__content > *.active img') ||
      modal.querySelector('.product-media-modal__content img');
  }

  function applyModalZoom(img, scale, originX, originY) {
    if (!img) return;
    img.classList.add('gf-zoom-target');
    img.style.transformOrigin = originX + '% ' + originY + '%';
    img.style.transform = 'scale(' + scale + ')';
    img.dataset.gfZoom = String(scale);
  }

  function resetModalZoom(modal) {
    if (!modal) return;
    modal.querySelectorAll('.gf-zoom-target').forEach(function (img) {
      img.style.transform = '';
      img.style.transformOrigin = '';
      img.dataset.gfZoom = '1';
    });
  }

  function initModalZoom(modal) {
    if (!modal || modal.hasAttribute('data-gf-zoom-init')) return;
    modal.setAttribute('data-gf-zoom-init', 'true');

    var lastTap = 0;
    var pinchStart = 0;
    var pinchScale = 1;

    modal.addEventListener(
      'dblclick',
      function (e) {
        var img = e.target.closest('img');
        if (!img || !modal.contains(img)) return;
        e.preventDefault();
        var current = parseFloat(img.dataset.gfZoom || '1');
        if (current > 1.1) {
          applyModalZoom(img, 1, 50, 50);
        } else {
          var rect = img.getBoundingClientRect();
          var ox = ((e.clientX - rect.left) / rect.width) * 100;
          var oy = ((e.clientY - rect.top) / rect.height) * 100;
          applyModalZoom(img, 2.2, ox, oy);
        }
      }
    );

    modal.addEventListener(
      'touchend',
      function (e) {
        if (e.changedTouches.length !== 1) return;
        var now = Date.now();
        if (now - lastTap < 280) {
          var img = getModalZoomImage(modal);
          if (!img) return;
          var current = parseFloat(img.dataset.gfZoom || '1');
          applyModalZoom(img, current > 1.1 ? 1 : 2.2, 50, 50);
        }
        lastTap = now;
      },
      { passive: true }
    );

    modal.addEventListener(
      'wheel',
      function (e) {
        if (!modal.hasAttribute('open')) return;
        var img = getModalZoomImage(modal);
        if (!img) return;
        e.preventDefault();
        var current = parseFloat(img.dataset.gfZoom || '1');
        var next = e.deltaY < 0 ? current + 0.15 : current - 0.15;
        next = Math.max(1, Math.min(3, next));
        applyModalZoom(img, next, 50, 50);
      },
      { passive: false }
    );

    modal.addEventListener(
      'touchstart',
      function (e) {
        if (e.touches.length === 2) {
          var dx = e.touches[0].clientX - e.touches[1].clientX;
          var dy = e.touches[0].clientY - e.touches[1].clientY;
          pinchStart = Math.hypot(dx, dy);
          var img = getModalZoomImage(modal);
          pinchScale = img ? parseFloat(img.dataset.gfZoom || '1') : 1;
        }
      },
      { passive: true }
    );

    modal.addEventListener(
      'touchmove',
      function (e) {
        if (e.touches.length !== 2 || !pinchStart) return;
        var dx = e.touches[0].clientX - e.touches[1].clientX;
        var dy = e.touches[0].clientY - e.touches[1].clientY;
        var dist = Math.hypot(dx, dy);
        var img = getModalZoomImage(modal);
        if (!img) return;
        var next = Math.max(1, Math.min(3, pinchScale * (dist / pinchStart)));
        applyModalZoom(img, next, 50, 50);
      },
      { passive: true }
    );
  }

  /* ── D. Thumbnail drag-scroll (desktop) ──────────────────── */

  function initThumbnailDragScroll(gallery) {
    var thumbnailSlider = gallery.querySelector('[id^="GalleryThumbnails-"]');
    if (!thumbnailSlider || thumbnailSlider.hasAttribute('data-gf-drag-init')) return;
    thumbnailSlider.setAttribute('data-gf-drag-init', 'true');

    var list = thumbnailSlider.querySelector('.thumbnail-list');
    if (!list) return;

    var isDragging = false;
    var startX = 0;
    var scrollStartLeft = 0;
    var dragDistance = 0;

    list.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      isDragging = true;
      dragDistance = 0;
      startX = e.clientX;
      scrollStartLeft = list.scrollLeft;
      list.setPointerCapture(e.pointerId);
      list.style.cursor = 'grabbing';
      list.style.userSelect = 'none';
    });

    list.addEventListener('pointermove', function (e) {
      if (!isDragging) return;
      var dx = e.clientX - startX;
      dragDistance = Math.abs(dx);
      list.scrollLeft = scrollStartLeft - dx;
    });

    function endDrag() {
      if (!isDragging) return;
      isDragging = false;
      list.style.cursor = '';
      list.style.userSelect = '';
      if (dragDistance >= 4) {
        var suppressClick = function (ev) {
          ev.stopPropagation();
          ev.preventDefault();
          list.removeEventListener('click', suppressClick, true);
        };
        list.addEventListener('click', suppressClick, true);
      }
    }

    list.addEventListener('pointerup', endDrag);
    list.addEventListener('pointercancel', endDrag);
  }

  /* ── E. Thumbnail preload ────────────────────────────────── */

  var preloadedSrcs = new Set();

  function initThumbnailPreload(gallery) {
    var thumbnailSlider = gallery.querySelector('[id^="GalleryThumbnails-"]');
    if (!thumbnailSlider || thumbnailSlider.hasAttribute('data-gf-preload-init')) return;
    thumbnailSlider.setAttribute('data-gf-preload-init', 'true');

    thumbnailSlider.querySelectorAll('.thumbnail-list__item').forEach(function (item) {
      var btn = item.querySelector('button.thumbnail');
      if (!btn) return;

      btn.addEventListener(
        'mouseenter',
        function () {
          var targetId = item.dataset.target;
          if (!targetId) return;
          var viewer = gallery.querySelector('[id^="GalleryViewer-"]');
          if (!viewer) return;
          var slide = viewer.querySelector('[data-media-id="' + targetId + '"]');
          if (!slide) return;
          var img = slide.querySelector('.product__media img');
          if (!img || !img.src) return;
          var preloadHref = img.src;
          if (img.srcset) {
            var parts = img.srcset.split(',').map(function (s) {
              return s.trim().split(' ')[0];
            });
            if (parts.length) preloadHref = parts[parts.length - 1];
          }
          if (preloadedSrcs.has(preloadHref)) return;
          preloadedSrcs.add(preloadHref);
          var link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = preloadHref;
          document.head.appendChild(link);
        },
        { passive: true }
      );
    });
  }

  /* ── F. View Similar drawer ──────────────────────────────── */

  function getOrCreateScrim() {
    var scrim = document.querySelector('.gf-view-similar-scrim');
    if (scrim) return scrim;
    scrim = document.createElement('div');
    scrim.className = 'gf-view-similar-scrim';
    scrim.setAttribute('aria-hidden', 'true');
    document.body.appendChild(scrim);
    return scrim;
  }

  function portalDrawer(drawer) {
    if (!drawer || drawer.parentElement === document.body) return;
    document.body.appendChild(drawer);
  }

  function openDrawer(drawer, triggerBtn) {
    var scrim = getOrCreateScrim();
    portalDrawer(drawer);
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (triggerBtn) triggerBtn.setAttribute('aria-expanded', 'true');
    scrim.classList.add('is-visible');
    scrim.setAttribute('aria-hidden', 'false');
    document.body.classList.add('gf-similar-open');
    lockBodyScroll();

    var productId = drawer.dataset.productId;
    var currentId = drawer.dataset.currentProductId;
    var container = drawer.querySelector('.gf-view-similar-drawer__products');
    var hasContent = container && container.querySelector('.gf-similar-track .gf-similar-card');
    if (productId && container && (!drawer.dataset.loaded || !hasContent)) {
      loadSimilarProducts(productId, currentId, container, drawer);
    }

    var closeBtn = drawer.querySelector('.gf-view-similar-drawer__close');
    if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 50);
    initDrawerDrag(drawer);
  }

  function initDrawerDrag(drawer) {
    if (!drawer || drawer.hasAttribute('data-gf-drag-close')) return;
    drawer.setAttribute('data-gf-drag-close', 'true');
    var handle = drawer.querySelector('.gf-view-similar-drawer__header');
    if (!handle) return;
    var startY = 0;
    var dragging = false;

    handle.addEventListener(
      'touchstart',
      function (e) {
        dragging = true;
        startY = e.touches[0].clientY;
        drawer.style.transition = 'none';
      },
      { passive: true }
    );

    handle.addEventListener(
      'touchmove',
      function (e) {
        if (!dragging) return;
        var dy = e.touches[0].clientY - startY;
        if (dy > 0) drawer.style.transform = 'translateY(' + dy + 'px)';
      },
      { passive: true }
    );

    handle.addEventListener(
      'touchend',
      function (e) {
        if (!dragging) return;
        dragging = false;
        var dy = e.changedTouches[0].clientY - startY;
        drawer.style.transition = '';
        drawer.style.transform = '';
        if (dy > 88) {
          var trigger = document.querySelector('[aria-controls="' + drawer.id + '"]');
          closeDrawer(drawer, trigger);
        }
      },
      { passive: true }
    );
  }

  function closeDrawer(drawer, triggerBtn) {
    var scrim = document.querySelector('.gf-view-similar-scrim');
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (triggerBtn) {
      triggerBtn.setAttribute('aria-expanded', 'false');
      triggerBtn.focus();
    }
    if (scrim) {
      scrim.classList.remove('is-visible');
      scrim.setAttribute('aria-hidden', 'true');
    }
    document.body.classList.remove('gf-similar-open');
    unlockBodyScroll();
  }

  var similarPageShieldUntil = 0;

  function getOpenSimilarDrawer() {
    return document.querySelector('.gf-view-similar-drawer.is-open');
  }

  function isInsideSimilarSheet(target) {
    return !!(target && target.closest && target.closest('.gf-view-similar-drawer'));
  }

  function closeOpenSimilarDrawer() {
    var openEl = getOpenSimilarDrawer();
    if (!openEl) return false;
    var trigger = document.querySelector('[aria-controls="' + openEl.id + '"]');
    closeDrawer(openEl, trigger);
    similarPageShieldUntil = Date.now() + 450;
    return true;
  }

  function blockUnderlyingPageEvent(e) {
    var openEl = getOpenSimilarDrawer();
    var shielded = Date.now() < similarPageShieldUntil;
    if (!openEl && !shielded) return false;
    if (openEl && isInsideSimilarSheet(e.target)) return false;
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    if (openEl) closeOpenSimilarDrawer();
    return true;
  }

  function loadSimilarProducts(productId, currentProductId, container, drawer) {
    drawer._gfLoadId = (drawer._gfLoadId || 0) + 1;
    var loadId = drawer._gfLoadId;
    delete drawer.dataset.loaded;

    container.innerHTML = '<p class="gf-similar-status">Loading…</p>';

    var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    var ctx = {
      productType: (drawer.dataset.productType || '').trim(),
      tags: (drawer.dataset.productTags || '').split('|').filter(Boolean),
      collectionHandles: (drawer.dataset.collections || '')
        .split(',')
        .map(function (s) {
          return s.trim();
        })
        .filter(Boolean),
    };

    var collectionFetches = ctx.collectionHandles.slice(0, 3).map(function (handle) {
      return fetch(root + 'collections/' + encodeURIComponent(handle) + '/products.json?limit=20')
        .then(function (r) {
          return r.ok ? r.json() : { products: [] };
        })
        .then(function (data) {
          return data.products || [];
        })
        .catch(function () {
          return [];
        });
    });

    var recommendationsFetch = fetch(
      root +
        'recommendations/products.json?product_id=' +
        encodeURIComponent(productId) +
        '&limit=15&intent=related'
    )
      .then(function (r) {
        return r.ok ? r.json() : { products: [] };
      })
      .then(function (data) {
        return data.products || [];
      })
      .catch(function () {
        return [];
      });

    Promise.all(collectionFetches.concat([recommendationsFetch]))
      .then(function (results) {
        if (loadId !== drawer._gfLoadId) return;

        var recommended = results.length ? results[results.length - 1] : [];
        var collectionResults = results.slice(0, -1);
        var fromCollections = [];

        collectionResults.forEach(function (list) {
          fromCollections = mergeSimilarProducts(fromCollections, list);
        });

        fromCollections = fromCollections.filter(function (p) {
          return String(p.id) !== String(currentProductId);
        });

        recommended = recommended.filter(function (p) {
          return String(p.id) !== String(currentProductId);
        });

        var products = rankSimilarProducts(fromCollections, recommended, ctx)
          .filter(function (p) {
            return p && p.title && (getProductImageUrl(p) || p.handle);
          })
          .slice(0, 15);

        if (loadId !== drawer._gfLoadId) return;

        if (!products.length) {
          container.innerHTML = '<p class="gf-similar-status">No similar products found.</p>';
          return;
        }

        renderSimilarProducts(products, container, root);
        drawer.dataset.loaded = 'true';
      })
      .catch(function () {
        if (loadId !== drawer._gfLoadId) return;
        delete drawer.dataset.loaded;
        container.innerHTML =
          '<p class="gf-similar-status gf-similar-status--error">Could not load similar products.</p>';
      });
  }

  function getProductImageUrl(product) {
    if (product.featured_image) return product.featured_image;
    if (product.images && product.images.length) {
      var first = product.images[0];
      if (typeof first === 'string') return first;
      return first.src || first.url || '';
    }
    if (product.image && product.image.src) return product.image.src;
    return '';
  }

  function shopifyImageUrl(url, width) {
    if (!url) return '';
    var raw = String(url);
    if (raw.indexOf('//') === 0) raw = 'https:' + raw;
    var parts = raw.split('?');
    var path = parts[0];
    var query = parts[1] || '';
    path = path.replace(
      /_(pico|icon|thumb|small|compact|medium|large|grande|original|\d+x\d*)(?=\.[a-zA-Z]+$)/,
      ''
    );
    var params = [];
    var version = query.match(/(?:^|&)v=([^&]+)/);
    if (version) params.push('v=' + version[1]);
    if (width) params.push('width=' + width);
    return path + (params.length ? '?' + params.join('&') : '');
  }

  function similarCardImageHtml(url, alt) {
    if (!url) {
      return '<div class="gf-similar-card__img gf-similar-card__img--placeholder"></div>';
    }
    var widths = [360, 480, 720, 960];
    var srcset = widths
      .map(function (width) {
        return shopifyImageUrl(url, width) + ' ' + width + 'w';
      })
      .join(', ');
    return (
      '<img class="gf-similar-card__img" src="' +
      escapeHtml(shopifyImageUrl(url, 720)) +
      '" srcset="' +
      escapeHtml(srcset) +
      '" sizes="(max-width: 749px) 160px, 200px" alt="' +
      escapeHtml(alt || '') +
      '" loading="lazy" decoding="async" width="720" height="900">'
    );
  }

  function getSimilarProductPrices(product, variant) {
    var priceCents = null;
    var compareAtCents = null;

    if (variant) {
      priceCents = shopifyPriceToCents(variant.price);
      compareAtCents = shopifyPriceToCents(variant.compare_at_price);
    }

    if (priceCents == null && product.price_min != null && product.price_min !== '') {
      priceCents = shopifyPriceToCents(product.price_min);
    }

    if (priceCents == null && product.variants && product.variants.length) {
      product.variants.forEach(function (v) {
        var variantPriceCents = shopifyPriceToCents(v.price);
        if (variantPriceCents != null && (priceCents == null || variantPriceCents < priceCents)) {
          priceCents = variantPriceCents;
        }
      });
    }

    if (
      compareAtCents == null &&
      product.compare_at_price_min != null &&
      product.compare_at_price_min !== '' &&
      Number(product.compare_at_price_min) > 0
    ) {
      compareAtCents = shopifyPriceToCents(product.compare_at_price_min);
    }

    if (compareAtCents == null && product.variants && product.variants.length) {
      product.variants.forEach(function (v) {
        var variantCompareAtCents = shopifyPriceToCents(v.compare_at_price);
        if (
          variantCompareAtCents != null &&
          variantCompareAtCents > 0 &&
          (compareAtCents == null || variantCompareAtCents < compareAtCents)
        ) {
          compareAtCents = variantCompareAtCents;
        }
      });
    }

    return { priceCents: priceCents, compareAtCents: compareAtCents };
  }

  function getProductUrl(product, root) {
    var base = (root || '/').replace(/\/?$/, '/');
    if (product.url) {
      if (product.url.indexOf('http') === 0) return product.url;
      if (product.url.charAt(0) === '/') return base.replace(/\/$/, '') + product.url;
      return base + product.url;
    }
    if (product.handle) return base + 'products/' + product.handle;
    return '#';
  }

  function mergeSimilarProducts(existing, incoming) {
    var map = {};
    existing.forEach(function (p) {
      map[p.id] = p;
    });
    incoming.forEach(function (p) {
      if (!map[p.id]) map[p.id] = p;
    });
    return Object.keys(map).map(function (id) {
      return map[id];
    });
  }

  function isRelevantSimilarProduct(product, ctx) {
    if (
      ctx.productType &&
      product.product_type &&
      product.product_type.toLowerCase() === ctx.productType.toLowerCase()
    ) {
      return true;
    }
    if (ctx.tags.length && product.tags && product.tags.length) {
      for (var i = 0; i < product.tags.length; i++) {
        if (ctx.tags.indexOf(product.tags[i]) !== -1) return true;
      }
    }
    return false;
  }

  function scoreSimilarProduct(product, ctx, collectionIds) {
    var score = 0;
    if (collectionIds[product.id]) score += 30;
    if (
      ctx.productType &&
      product.product_type &&
      product.product_type.toLowerCase() === ctx.productType.toLowerCase()
    ) {
      score += 20;
    }
    if (ctx.tags.length && product.tags) {
      product.tags.forEach(function (tag) {
        if (ctx.tags.indexOf(tag) !== -1) score += 5;
      });
    }
    return score;
  }

  function rankSimilarProducts(fromCollections, recommended, ctx) {
    var collectionIds = {};
    fromCollections.forEach(function (p) {
      collectionIds[p.id] = true;
    });

    var merged = mergeSimilarProducts(fromCollections, []);

    recommended.forEach(function (p) {
      var exists = merged.some(function (item) {
        return item.id === p.id;
      });
      if (exists) return;
      if (isRelevantSimilarProduct(p, ctx)) {
        merged.push(p);
      }
    });

    /* Last resort: only if no collection/relevant matches at all */
    if (merged.length === 0) {
      recommended.forEach(function (p) {
        var exists = merged.some(function (item) {
          return item.id === p.id;
        });
        if (!exists) merged.push(p);
      });
    }

    merged.sort(function (a, b) {
      return scoreSimilarProduct(b, ctx, collectionIds) - scoreSimilarProduct(a, ctx, collectionIds);
    });

    return merged;
  }

  function calcDiscount(priceCents, compareAtCents) {
    if (!compareAtCents || compareAtCents <= priceCents) return 0;
    return Math.round(((compareAtCents - priceCents) / compareAtCents) * 100);
  }

  function getFirstAvailableVariant(product) {
    if (!product.variants || !product.variants.length) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) return product.variants[i];
    }
    return product.variants[0];
  }

  function drawerRatingsMap(container) {
    var root =
      (container && container.closest('.gf-view-similar-drawer')) ||
      (container && container.closest('.gf-similar-products-inline')) ||
      (container && container.parentElement);
    if (!root) return {};
    if (root._gfRatings) return root._gfRatings;
    var map = {};
    var script = root.querySelector('[data-gf-similar-ratings]');
    if (script) {
      try {
        map = JSON.parse(script.textContent || '{}') || {};
      } catch (err) {
        map = {};
      }
    }
    root._gfRatings = map;
    return map;
  }

  function similarStarHtml(container) {
    var root =
      (container && container.closest('.gf-view-similar-drawer')) ||
      (container && container.closest('.gf-similar-products-inline')) ||
      document;
    var tpl = root.querySelector('.gf-star-template .gf-pdp-icon') || document.querySelector('.gf-rating-star .gf-pdp-icon');
    return tpl ? tpl.outerHTML : '';
  }

  function renderSimilarProducts(products, container, root) {
    var track = document.createElement('div');
    track.className = 'gf-similar-track';
    track.setAttribute('role', 'list');

    products.forEach(function (p) {
      var variant = getFirstAvailableVariant(p);
      var prices = getSimilarProductPrices(p, variant);
      var priceCents = prices.priceCents;
      var compareAtCents = prices.compareAtCents;
      var discount = priceCents != null ? calcDiscount(priceCents, compareAtCents) : 0;
      var imageUrl = getProductImageUrl(p);
      var productUrl = getProductUrl(p, root);
      var card = document.createElement('article');
      card.className = 'gf-similar-card';
      card.setAttribute('role', 'listitem');

      var imgHtml = similarCardImageHtml(imageUrl, p.title);

      var priceHtml = '';
      if (priceCents != null) {
        if (compareAtCents != null && compareAtCents > priceCents) {
          priceHtml +=
            '<span class="gf-similar-card__mrp">' + formatSimilarMoney(compareAtCents) + '</span>';
        }
        priceHtml += '<span class="gf-similar-card__price">' + formatSimilarMoney(priceCents) + '</span>';
        if (discount > 0) {
          priceHtml += '<span class="gf-similar-card__discount">' + discount + '% OFF</span>';
        }
      }

      var addLabel = variant && variant.available ? 'Add to Bag' : 'Sold Out';
      var addDisabled = !variant || !variant.available ? ' disabled' : '';
      var ratings = drawerRatingsMap(container);
      var ratingInfo = ratings[String(p.id)] || ratings[p.id];
      var ratingHtml = '';
      var starHtml = similarStarHtml(container);
      if (ratingInfo && ratingInfo.rating && starHtml) {
        ratingHtml =
          '<p class="gf-similar-card__rating"><span>' +
          escapeHtml(String(ratingInfo.rating)) +
          '</span>' +
          starHtml +
          (ratingInfo.count
            ? '<span class="gf-similar-card__count">' + escapeHtml(String(ratingInfo.count)) + '</span>'
            : '') +
          '</p>';
      }

      card.innerHTML =
        '<a class="gf-similar-card__link" href="' +
        escapeHtml(productUrl) +
        '">' +
        '<div class="gf-similar-card__media">' +
        imgHtml +
        '</div>' +
        ratingHtml +
        '<p class="gf-similar-card__vendor">' +
        escapeHtml(p.vendor) +
        '</p>' +
        '<p class="gf-similar-card__title">' +
        escapeHtml(p.title) +
        '</p>' +
        '<div class="gf-similar-card__pricing">' +
        priceHtml +
        '</div>' +
        '</a>' +
        '<button type="button" class="gf-similar-card__atc"' +
        (variant ? ' data-variant-id="' + String(variant.id) + '"' : '') +
        addDisabled +
        '>' +
        addLabel +
        '</button>';

      track.appendChild(card);
    });

    if (!track.children.length) {
      container.innerHTML = '<p class="gf-similar-status">No similar products found.</p>';
      return;
    }

    container.innerHTML = '';
    container.appendChild(track);
  }

  function addSimilarToCart(variantId, btn) {
    if (!variantId || !window.routes || !window.routes.cart_add_url) return;

    btn.disabled = true;
    btn.classList.add('is-loading');
    var originalText = btn.textContent;

    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');

    var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.append(
        'sections',
        cart.getSectionsToRender().map(function (s) {
          return s.id;
        })
      );
      body.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(btn);
    }

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body: body,
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        if (data.status) throw new Error(data.description || 'Could not add to cart');
        btn.textContent = 'Added!';
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'gf-similar',
            productVariantId: variantId,
            cartData: data,
          });
        }

        if (cart && typeof cart.renderContents === 'function' && data.sections) {
          cart.renderContents(data);
        }

        setTimeout(function () {
          btn.textContent = originalText;
          btn.classList.remove('is-added');
          btn.disabled = false;
        }, 1800);
      })
      .catch(function () {
        btn.textContent = 'Try again';
        btn.classList.remove('is-loading');
        setTimeout(function () {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 1500);
      });
  }

  /* ── G. Rating scroll to reviews ─────────────────────────── */

  function ensureReviewsAnchor() {
    var target =
      document.getElementById('gf-reviews-anchor') ||
      document.querySelector('.jdgm-review-widget') ||
      document.querySelector('#judgeme_product_reviews') ||
      document.querySelector('[class*="jdgm-widget"]') ||
      document.querySelector('section[id*="1784615490"]');

    if (!target) {
      var sections = document.querySelectorAll('.shopify-section');
      for (var i = 0; i < sections.length; i++) {
        if (sections[i].querySelector('.jdgm-review-widget, [class*="jdgm"]')) {
          target = sections[i];
          break;
        }
      }
    }

    if (target && !target.id) target.id = 'gf-reviews-anchor';
    return target;
  }

  function scrollToReviews() {
    var openDrawerEl = document.querySelector('.gf-view-similar-drawer.is-open');
    if (openDrawerEl) {
      var triggerBtn = document.querySelector('[aria-controls="' + openDrawerEl.id + '"]');
      closeDrawer(openDrawerEl, triggerBtn);
    }

    var target = ensureReviewsAnchor();
    if (!target) return;

    var headerOffset = 80;
    var top = target.getBoundingClientRect().top + window.scrollY - headerOffset;
    window.scrollTo({ top: top, behavior: 'smooth' });
  }

  function handleBuyNow(btn) {
    if (btn.disabled) return;
    var formId = btn.getAttribute('data-product-form');
    var form = (formId && document.getElementById(formId)) || document.querySelector('product-info[id^="MainProduct-"] form[action*="/cart/add"]');
    if (!form) return;

    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    var addUrl = (window.routes && window.routes.cart_add_url) || root + 'cart/add';
    var checkoutUrl = root.replace(/\/?$/, '/') + 'checkout';
    var body = new FormData(form);
    btn.disabled = true;

    fetch(addUrl, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body: body,
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok || data.status) throw new Error(data.description || 'Could not add to cart');
          window.location.href = checkoutUrl;
        });
      })
      .catch(function () {
        btn.disabled = false;
        form.submit();
      });
  }

  /* ── Share (preserved from prior milestone; delegated) ───── */

  function handleShare(btn) {
    var url = btn.dataset.shareUrl || window.location.href;
    var title = btn.dataset.shareTitle || document.title;

    if (navigator.share) {
      navigator.share({ title: title, url: url }).catch(function () {});
      return;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(function () {
        showShareToast('Link copied!');
      });
      return;
    }

    showShareToast('Share: ' + url);
  }

  function showShareToast(msg) {
    var existing = document.querySelector('.gf-share-toast');
    if (existing) existing.remove();
    var toast = document.createElement('div');
    toast.className = 'gf-share-toast';
    toast.textContent = msg;
    document.body.appendChild(toast);
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () { toast.remove(); }, 300);
    }, 2200);
  }

  /* ── H. Global delegated events (once) ───────────────────── */

  function initGlobalEvents() {
    if (document.documentElement.hasAttribute(ROOT_INIT)) return;
    document.documentElement.setAttribute(ROOT_INIT, 'true');

    ['pointerdown', 'mousedown', 'touchstart', 'click'].forEach(function (type) {
      document.addEventListener(type, blockUnderlyingPageEvent, { capture: true, passive: false });
    });

    document.addEventListener('click', function (e) {
      var similarBtn = e.target.closest('.gf-view-similar-btn');
      if (similarBtn) {
        e.preventDefault();
        var drawerId = similarBtn.getAttribute('aria-controls');
        var drawer = drawerId ? document.getElementById(drawerId) : null;
        if (drawer) openDrawer(drawer, similarBtn);
        return;
      }

      var soundBtn = e.target.closest('[data-gf-video-sound]');
      if (soundBtn) {
        e.preventDefault();
        e.stopPropagation();
        toggleVideoSound(soundBtn);
        return;
      }

      var backBtn = e.target.closest('[data-gf-pdp-back]');
      if (backBtn) {
        e.preventDefault();
        if (window.history.length > 1) {
          window.history.back();
        } else {
          window.location.href = backBtn.getAttribute('data-gf-pdp-home') || '/';
        }
        return;
      }

      var closeBtn = e.target.closest('[data-close-drawer]');
      if (closeBtn) {
        e.preventDefault();
        var drawerEl = document.getElementById(closeBtn.getAttribute('data-close-drawer'));
        if (drawerEl) {
          var trigger = document.querySelector('[aria-controls="' + drawerEl.id + '"]');
          closeDrawer(drawerEl, trigger);
        }
        return;
      }

      if (e.target.closest('.gf-view-similar-scrim')) {
        var openEl = document.querySelector('.gf-view-similar-drawer.is-open');
        if (openEl) {
          var trig = document.querySelector('[aria-controls="' + openEl.id + '"]');
          closeDrawer(openEl, trig);
        }
        return;
      }

      var ratingBtn = e.target.closest('.gf-rating-badge');
      if (ratingBtn) {
        e.preventDefault();
        scrollToReviews();
        return;
      }

      var atcBtn = e.target.closest('.gf-similar-card__atc');
      if (atcBtn && atcBtn.dataset.variantId) {
        e.preventDefault();
        addSimilarToCart(atcBtn.dataset.variantId, atcBtn);
        return;
      }

      var shareBtn = e.target.closest('.gf-share-btn');
      if (shareBtn && !shareBtn.hasAttribute('data-gfm-wish-btn')) {
        e.preventDefault();
        handleShare(shareBtn);
        return;
      }

      var buyNowBtn = e.target.closest('[data-gf-buy-now]');
      if (buyNowBtn) {
        e.preventDefault();
        handleBuyNow(buyNowBtn);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var openDrawerEl = document.querySelector('.gf-view-similar-drawer.is-open');
      if (openDrawerEl) {
        e.preventDefault();
        var triggerBtn = document.querySelector('[aria-controls="' + openDrawerEl.id + '"]');
        closeDrawer(openDrawerEl, triggerBtn);
        return;
      }
      var openSearch = document.querySelector('.gf-product-page-header details[open]');
      if (openSearch) {
        openSearch.removeAttribute('open');
      }
    });
  }

  /* ── I. Per-gallery init / teardown ──────────────────────── */

  function initGallery(gallery) {
    if (gallery.hasAttribute(GALLERY_INIT)) return;
    gallery.setAttribute(GALLERY_INIT, 'true');

    initDots(gallery);
    initViewSimilarCollapse(gallery);
    initThumbnailOverflow(gallery);
    initLightboxEnhancements(gallery);
    initThumbnailDragScroll(gallery);
    initThumbnailPreload(gallery);

    var drawer = document.getElementById('GfViewSimilarDrawer-' + getGalleryId(gallery));
    if (drawer) portalDrawer(drawer);
  }

  function teardownGallery(gallery) {
    gallery.removeAttribute(GALLERY_INIT);

    if (gallery._gfDotsObserver) {
      gallery._gfDotsObserver.disconnect();
      gallery._gfDotsObserver = null;
    }
    if (gallery._gfThumbRO) {
      gallery._gfThumbRO.disconnect();
      gallery._gfThumbRO = null;
    }
    if (gallery._gfLightboxMO) {
      gallery._gfLightboxMO.disconnect();
      gallery._gfLightboxMO = null;
    }
    if (gallery._gfSlideHandler) {
      var viewer = gallery.querySelector('[id^="GalleryViewer-"]');
      if (viewer) viewer.removeEventListener('slideChanged', gallery._gfSlideHandler);
      gallery._gfSlideHandler = null;
    }

    var similarBtn = gallery.querySelector('.gf-view-similar-btn');
    if (similarBtn) similarBtn.classList.remove('is-icon-only');

    var dotsContainer = gallery.querySelector('.gf-slide-dots');
    if (dotsContainer) dotsContainer.innerHTML = '';
  }

  function initInlineSimilar() {
    document.querySelectorAll('.gf-similar-products-inline[data-product-id]').forEach(function (el) {
      var container = el.querySelector('.gf-view-similar-drawer__products');
      if (!container) return;

      var hasContent = container.querySelector('.gf-similar-track .gf-similar-card');
      if (el.dataset.loaded && hasContent) return;

      var productId = el.dataset.productId;
      var currentId = el.dataset.currentProductId || productId;
      if (productId) loadSimilarProducts(productId, currentId, container, el);
    });
  }

  function bootAll() {
    initGlobalEvents();
    ensureReviewsAnchor();
    document.querySelectorAll('media-gallery').forEach(initGallery);
    document.querySelectorAll('.gf-view-similar-drawer').forEach(portalDrawer);
    initInlineSimilar();
  }

  function handleSectionLoad(container) {
    (container || document).querySelectorAll('media-gallery').forEach(function (gallery) {
      teardownGallery(gallery);
      initGallery(gallery);
    });
    ensureReviewsAnchor();
    initInlineSimilar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  window.addEventListener('load', initInlineSimilar);

  document.addEventListener('shopify:section:load', function (e) {
    handleSectionLoad(e.target);
  });
})();
